import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  CssBaseline,
  FormControl,
  Input,
  InputLabel,
  Paper,
  Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/LockReset';
import anearApi from '../Api/AnearApi';
import useNavigation from '../Utils/navigationUtils';
import { CONTENT_COLUMN_MAX_WIDTH, contentColumnPageShellSx } from '../UI/contentColumn'
import PageBackRow from '../UI/PageBackRow'

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const { navigateToLogin } = useNavigation();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);
      await anearApi.requestPasswordReset(email);
      setRequestSent(true);
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={contentColumnPageShellSx}>
      <CssBaseline />
      <Paper
        sx={{
          mt: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          px: 3,
          pb: 3,
          bgcolor: '#0B0F19',
          color: '#E6E9EF',
          borderRadius: 3,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: CONTENT_COLUMN_MAX_WIDTH
        }}
      >
        <PageBackRow onClick={navigateToLogin} label="Back to login" sx={{ mb: 1 }} />
        <Avatar sx={{ m: 1, backgroundColor: 'rgba(124, 58, 237, 0.3)', color: '#A78BFA' }}>
          <LockIcon />
        </Avatar>
        <Typography component="h1" variant="h5" sx={{ color: '#E6E9EF', fontWeight: 800, letterSpacing: 0.2 }}>
          Reset password
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 1, color: 'rgba(230,233,239,0.82)', textAlign: 'center', maxWidth: '30rem' }}
        >
          Enter the email address for your Anear account and we will send you a reset link.
        </Typography>

        {!requestSent && (
          <Box component="form" sx={{ width: '100%', mt: 2 }} autoComplete="off">
            <FormControl
              required
              fullWidth
              error={error !== null}
              sx={{ mb: 2, '& label + .MuiInputBase-root': { marginTop: 0 } }}
              variant="standard"
            >
              <InputLabel
                htmlFor="email"
                shrink={true}
                sx={{
                  color: '#06B6D4',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  transform: 'none',
                  position: 'static',
                  mb: 0.25
                }}
              >
                Email Address
              </InputLabel>
              <Input
                id="email"
                name="email"
                required
                value={email}
                sx={{
                  color: '#E6E9EF',
                  marginTop: '0 !important',
                  '&::before': { borderBottom: '1px solid rgba(230,233,239,0.3)' },
                  '&:hover:not(.Mui-disabled)::before': { borderBottom: '1px solid rgba(230,233,239,0.5)' },
                  '&::after': { borderBottom: '2px solid #7C3AED' }
                }}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
              />
            </FormControl>

            {error && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)'
                }}
              >
                <Typography variant="body2" sx={{ color: '#EF4444', fontWeight: 500, lineHeight: 1.5 }}>
                  {error.message || 'Unable to send reset link. Please try again.'}
                </Typography>
              </Box>
            )}

            <Button
              sx={{
                mt: 3,
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: 999,
                px: 3,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(90deg,#7C3AED,#06B6D4)',
                boxShadow: '0 0 32px rgba(124,58,237,0.45)',
                '&:hover': {
                  background: 'linear-gradient(90deg,#6D28D9,#0891B2)',
                  boxShadow: '0 0 40px rgba(124,58,237,0.55)'
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)',
                  color: 'rgba(230,233,239,0.5)'
                }
              }}
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              onClick={handleSubmit}
            >
              Send reset link
            </Button>
          </Box>
        )}

        {requestSent && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              width: '100%',
              bgcolor: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.25)'
            }}
          >
            <Typography variant="body1" sx={{ color: '#D1FAE5', fontWeight: 600, textAlign: 'center' }}>
              If that email belongs to an account, a reset link is on its way.
            </Typography>
          </Box>
        )}

      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
